import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, HelpCircle, ChevronDown, ChevronUp, BookOpen, FlaskConical, Zap, Play, CheckCircle } from 'lucide-react';
import helpContent from '../helpContent';
import { useTour } from '../contexts/TourContext';
import Icon from './AppIcon';


const EXCLUDED_ROUTES = ['/login', '/register', '/email-confirmation', '/'];
const STORAGE_KEY = 'helpPanel_open';

function AccordionSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
          <Icon size={15} className="text-indigo-500" />
          {title}
        </span>
        {open
          ? <ChevronUp size={14} className="text-gray-400" />
          : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}

export default function HelpSidePanel() {
  const location = useLocation();
  const pathname = location?.pathname || '/';
  const tour = useTour();

  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const shouldShow = !EXCLUDED_ROUTES?.includes(pathname);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(isOpen)); } catch {}
  }, [isOpen]);

  if (!shouldShow) return null;

  const content = helpContent?.[pathname] || helpContent?.['/'];
  const screenName = content?.screenName || 'Ayuda';

  const tourCompleted = tour?.isTourCompleted?.();
  const tourActive = tour?.active;
  const tourStep = tour?.stepIndex;
  const tourTotal = tour?.totalSteps;

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[290] md:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}
      {/* Floating toggle button */}
      <button
        onClick={toggle}
        aria-label={isOpen ? 'Cerrar ayuda' : 'Abrir ayuda'}
        className="fixed bottom-6 right-6 z-[300] w-12 h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white shadow-lg shadow-indigo-500/40 flex items-center justify-center transition-all duration-200 hover:scale-105"
      >
        {isOpen ? <X size={20} /> : <HelpCircle size={20} />}
      </button>

      {/* Side panel */}
      <div
        className={[
          'fixed top-0 right-0 h-full z-[295] flex flex-col',
          'bg-white dark:bg-gray-800',
          'shadow-2xl shadow-black/20',
          'transition-transform duration-300 ease-in-out',
          'w-full md:w-[360px]',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        ]?.join(' ')}
        role="complementary"
        aria-label="Panel de ayuda"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-indigo-600 text-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <HelpCircle size={18} />
            <span className="font-semibold text-sm truncate">Ayuda — {screenName}</span>
          </div>
          <button
            onClick={close}
            aria-label="Cerrar panel de ayuda"
            className="p-1 rounded hover:bg-indigo-500 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">

          {/* ─── TOUR GUIADO SECTION (always visible) ─── */}
          <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700 bg-indigo-50 dark:bg-indigo-900/20">
            <div className="flex items-center gap-2 mb-2">
              <Play size={15} className="text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm font-bold text-gray-800 dark:text-gray-100">Tour Guiado</span>
            </div>

            {tourActive ? (
              /* Tour in progress */
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Paso {tourStep + 1} de {tourTotal}
                </p>
                {/* Progress bar */}
                <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mb-3">
                  <div
                    className="h-1.5 bg-indigo-600 rounded-full transition-all duration-300"
                    style={{ width: `${((tourStep + 1) / tourTotal) * 100}%` }}
                  />
                </div>
                <button
                  onClick={() => { close(); tour?.nextStep?.(); }}
                  className="w-full py-2 px-3 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                >
                  Continuar tour →
                </button>
              </div>
            ) : tourCompleted ? (
              /* Tour completed */
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <CheckCircle size={14} className="text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Tour completado ✓</span>
                </div>
                <button
                  onClick={() => { close(); tour?.resetTour?.(); }}
                  className="w-full py-2 px-3 text-xs font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                >
                  ↺ Repetir tour
                </button>
              </div>
            ) : (
              /* Tour not started */
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Recorre toda la app paso a paso</p>
                <button
                  onClick={() => { close(); tour?.startTour?.(); }}
                  className="w-full py-2.5 px-3 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                >
                  <Play size={12} />
                  ▶ Iniciar Tour Completo
                </button>
              </div>
            )}
          </div>

          {/* FAQs */}
          {content?.faqs?.length > 0 && (
            <AccordionSection title="Preguntas frecuentes" icon={BookOpen} defaultOpen={true}>
              <div className="space-y-3">
                {content?.faqs?.map((faq, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-gray-800 dark:text-gray-100 mb-1">{faq?.q}</p>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{faq?.a}</p>
                  </div>
                ))}
              </div>
            </AccordionSection>
          )}

          {/* Formulas */}
          {content?.formulas?.length > 0 && (
            <AccordionSection title="Fórmulas" icon={FlaskConical} defaultOpen={false}>
              <div className="space-y-2">
                {content?.formulas?.map((f, i) => (
                  <div key={i} className="text-sm">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{f?.name}</p>
                    <code className="block bg-gray-100 dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 rounded px-3 py-2 text-xs font-mono leading-relaxed break-all">
                      {f?.formula}
                    </code>
                  </div>
                ))}
              </div>
            </AccordionSection>
          )}

          {/* Quickstart */}
          {content?.quickstart && (
            <AccordionSection title="Ejemplo rápido" icon={Zap} defaultOpen={false}>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                {content?.quickstart}
              </p>
            </AccordionSection>
          )}

          {!content && (
            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
              No hay contenido de ayuda disponible para esta pantalla.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
          <a
            href="/help"
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
          >
            <BookOpen size={12} />
            Ver documentación completa
          </a>
        </div>
      </div>
    </>
  );
}
